import React from 'react'

import { CloudUploadIcon, UploadIcon } from 'lucide-react'
import { action, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import DndArea from '~/client/src/desktop/components/DndArea/DndArea'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { Button } from '~/client/src/shared/components/Button/Button'
import { FilePicker } from '~/client/src/shared/components/FilePicker'
import { Loader } from '~/client/src/shared/components/Loader'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import FileType from '~/client/src/shared/enums/FileType'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import MapViewItemsSetupStore from '../../../stores/MapViewItemsSetup.store'
import SitemapControlStore from '../../../stores/SitemapControl.store'

interface IProps {
  store: SitemapControlStore
  shouldFixTopPosition?: boolean

  mapViewItemsSetupStore?: MapViewItemsSetupStore
  state?: DesktopInitialState
}

const EXTENSIONS_SEPARATOR = ','
const TILESET_EXTENSIONS = [
  '.zip',
  '.geojson',
  '.geojsonld',
  '.ldgeojson',
  '.geojsonl',
  '.ndgeojson',
  '.tif',
  '.tiff',
]
const PLAN_EXTENSIONS = ['.pdf', '.png', '.jpeg', '.jpg']
const MAX_NAME_LENGTH = 64
const FILE_NAME_REGEX = /^[a-zA-Z0-9._\- ]+$/

@inject('state')
@observer
export default class UploadMapFileDialog extends React.Component<IProps> {
  @observable private isFileNameValid = true
  private get isSaveButtonActive(): boolean {
    const { tilesetFile, isUploading, sitemapName, basemapFile } =
      this.props.store
    return (
      (!!tilesetFile && !this.hasSomeError) ||
      (!isUploading && !!sitemapName && !!basemapFile)
    )
  }

  private get isTilesetsDisabled(): boolean {
    return this.props.state.isTilesetsDisabled
  }

  private get acceptedExtensions(): string[] {
    return this.isTilesetsDisabled
      ? [...PLAN_EXTENSIONS]
      : [...TILESET_EXTENSIONS, ...PLAN_EXTENSIONS]
  }

  private get acceptedExtensionsText(): string {
    return Localization.translator.mapFileUpload.acceptedExtensions(
      this.acceptedExtensions.join(`${EXTENSIONS_SEPARATOR} `),
    )
  }

  public componentWillUnmount(): void {
    this.props.store.resetTileFile()
  }

  public render(): JSX.Element {
    const { shouldFixTopPosition } = this.props
    const {
      isUploading,
      basemapFile,
      sitemapName,
      planCreationPdfFile,
      toggleCreateMapFileMenu,
      uploadTileFile,
      setPdfOrCreateSitemap,
      changeMapName,
    } = this.props.store

    const onSave = basemapFile
      ? setPdfOrCreateSitemap.bind(this, false)
      : uploadTileFile

    return (
      <MenuCloser
        className={classList({
          'absolute map-upload-block tilesets-upload': true,
          'fixed-top': shouldFixTopPosition,
        })}
        closeMenu={toggleCreateMapFileMenu}
      >
        <div className="col">
          <div className="text size24 mb30">
            {Localization.translator.mapFileUpload.uploadMapFile}
          </div>
          <div className="row y-start">
            <div>
              <div className="general-map-setup-dnd-area">
                <DndArea
                  fileType={FileType.Image}
                  accept={this.acceptedExtensions}
                  title={this.title}
                  onDrop={this.setMapFile}
                  className="upload-schedule-dnd-container col x-center y-center"
                />
                {this.renderBrowseComputerLine()}
              </div>
            </div>
          </div>
          {!basemapFile && !this.isTilesetsDisabled && (
            <div className="text large primary mt5">{this.limitsText}</div>
          )}
          {basemapFile && (
            <div className="row mt5 relative overflow-hidden">
              <StruxhubInput
                id="sitemap-name-input"
                label={Localization.translator.mapFileUpload.planName}
                isRequired={true}
                value={sitemapName}
                onChange={changeMapName}
              />
            </div>
          )}
          <div
            className={classList({
              'submit-button light pa10 full-width mt20': true,
              'inactive-element unclickable-element': !this.isSaveButtonActive,
            })}
            onClick={onSave}
          >
            {isUploading ? (
              <Loader />
            ) : planCreationPdfFile ? (
              Localization.translator.mapFileUpload.selectPage
            ) : (
              Localization.translator.save
            )}
          </div>
          {this.hasSomeError && (
            <div className="text red bold center mt5">{this.errorText}</div>
          )}
        </div>
      </MenuCloser>
    )
  }

  private get limitsText(): string {
    const { maxNumber, maxSize } = this.props.state.tilesetsConfiguration
    return Localization.translator.mapFileUpload.tilesetLimits(
      maxNumber,
      maxSize,
    )
  }

  private get errorText(): string {
    if (!this.isSizeValid) {
      return Localization.translator.mapFileUpload.fileTooLarge
    }
    if (!this.canUploadMoreTilesets) {
      return Localization.translator.mapFileUpload.tooManyTilesets
    }
    if (!this.isTilesetNameValid) {
      return Localization.translator.mapFileUpload.tilesetNameTooLong(
        MAX_NAME_LENGTH,
      )
    }
    if (!this.isFileNameValid) {
      return Localization.translator.mapFileUpload.invalidFileName
    }
  }

  private get hasSomeError() {
    return (
      !this.isSizeValid ||
      !this.canUploadMoreTilesets ||
      !this.isTilesetNameValid ||
      !this.isFileNameValid
    )
  }

  private get title(): string | JSX.Element {
    const { isUploading, tilesetFile, planCreationPdfFile, basemapFile } =
      this.props.store
    if (isUploading) {
      return (
        <Loader hint={Localization.translator.mapFileUpload.uploadMapFile} />
      )
    }
    if (tilesetFile) {
      return (
        <div className="text large">
          {Localization.translator.mapFileUpload.mapFileUploaded}{' '}
          {tilesetFile.name}
        </div>
      )
    }
    if (basemapFile) {
      return (
        <div className="text large">
          {Localization.translator.mapFileUpload.planUploaded}{' '}
          {basemapFile.name}
        </div>
      )
    }
    if (planCreationPdfFile) {
      return (
        <div className="text large">
          {Localization.translator.mapFileUpload.planUploaded}{' '}
          {planCreationPdfFile.name}
        </div>
      )
    }

    return (
      <div className="col text large no-grow x-center">
        <span>
          <CloudUploadIcon size={20} className="tw:align-middle" />{' '}
          {Localization.translator.mapFileUpload.dragMapFileHere}
        </span>
        <span>{this.acceptedExtensionsText}</span>
      </div>
    )
  }

  private renderBrowseComputerLine = (): JSX.Element => {
    return (
      <FilePicker
        accept={this.acceptedExtensions.join(EXTENSIONS_SEPARATOR)}
        onPick={([file]) => this.setMapFile(file)}
        render={<Button variant="outline" className="tw:w-full" />}
      >
        <UploadIcon data-icon="inline-start" />
        {this.props.store.basemapFile
          ? Localization.translator.change_verb
          : Localization.translator.uploadFile}
      </FilePicker>
    )
  }

  private setMapFile = (file: File): void => {
    this.isFileNameValid = true
    this.props.store.resetMapFIleSelection()
    const splittedName = file.name.split('.')
    if (splittedName?.length <= 1) {
      return
    }
    const extention = '.' + splittedName.at(-1)
    if (TILESET_EXTENSIONS.includes(extention)) {
      this.validateFileName(file.name)
      this.props.store.setTileFile(file)
    } else if (PLAN_EXTENSIONS.includes(extention)) {
      this.props.store.setSitemapBasemap(file)
    }
  }

  @action.bound
  private validateFileName(name: string): void {
    this.isFileNameValid = FILE_NAME_REGEX.test(name)
  }

  private get isTilesetNameValid(): boolean {
    const { tilesetFile } = this.props.store
    return !tilesetFile || tilesetFile.name.length < MAX_NAME_LENGTH
  }

  private get isSizeValid(): boolean {
    const { tilesetFile } = this.props.store
    const { maxTilesetSizeInBytes } = this.props.state
    return !tilesetFile || tilesetFile.size <= maxTilesetSizeInBytes
  }

  private get canUploadMoreTilesets(): boolean {
    const { tilesetFile } = this.props.store
    const {
      tilesetsConfiguration: { maxNumber },
      activeTilesetsList,
    } = this.props.state
    return !tilesetFile || activeTilesetsList?.length < maxNumber
  }
}
